import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import Web3 from 'web3';
import Swal from 'sweetalert2';
import { AuthService } from '../auth.service';
import { AngularFirestore } from '@angular/fire/compat/firestore';

declare let window: any;

@Injectable({
  providedIn: 'root',
})
export class ConnectionsService {
  web3: any = null;

  chainIds: string[] = ['0x89']; // Polygon Mainet '0x89'
  chainId: string | null = null;
  addressUser: any = new BehaviorSubject<string>('');
  admin: any = new BehaviorSubject<boolean>(false);
  currentUser: any;
  private web3Initialized = false;
  private web3InitPromise: Promise<void> | null = null;

  constructor(private authSrv: AuthService, private afs: AngularFirestore) {
    this.authSrv.userAuth.subscribe((user: any) => {
      const previousUser = this.currentUser;
      this.currentUser = user != this.currentUser ? user : previousUser;
      if (this.currentUser !== previousUser) {
        this.afs
          .collection('users')
          .doc(this.currentUser.uid)
          .valueChanges()
          .subscribe((res: any) => {
            if (res !== undefined || res != null) this.init(res.hideBlockchain);
          });
      }
    });

    // Configurar listeners de MetaMask para detectar desconexiones
    this.setupMetaMaskListeners();
  }

  // Modificar el getter para asegurar inicialización
  get web3Instance() {
    if (!this.web3) {
      console.warn(
        'Acceso a web3 antes de inicialización, inicializando con HTTP'
      );
      this.web3 = new Web3(
        new Web3.providers.HttpProvider(
          'https://prettiest-orbital-arrow.matic.quiknode.pro/d3485a6223541986833f63906b4e455b852750a4/'
        )
      );
    }
    return this.web3;
  }

  // Método para asegurar que web3 está inicializado
  async ensureWeb3Initialized() {
    if (this.web3Initialized) {
      return;
    }

    if (!this.web3InitPromise) {
      this.web3InitPromise = new Promise(async (resolve) => {
        try {
          // Inicializar web3 con un proveedor HTTP como fallback
          this.web3 = new Web3(
            new Web3.providers.HttpProvider(
              'https://prettiest-orbital-arrow.matic.quiknode.pro/d3485a6223541986833f63906b4e455b852750a4/'
            )
          );

          // Intentar usar MetaMask si está disponible
          if (typeof window.ethereum !== 'undefined') {
            try {
              const isUnlocked = await window.ethereum?._metamask?.isUnlocked();
              if (isUnlocked) {
                this.web3 = new Web3(window.ethereum);
                // Intentar autoconectar
                await this.autoConnect();
              }
            } catch (e) {
              console.warn(
                'Error al inicializar con MetaMask, usando HTTP:',
                e
              );
            }
          }

          this.web3Initialized = true;
          resolve();
        } catch (error) {
          console.error('Error inicializando Web3:', error);
          // Asegurarse de que haya un fallback
          if (!this.web3) {
            this.web3 = new Web3(
              new Web3.providers.HttpProvider(
                'https://prettiest-orbital-arrow.matic.quiknode.pro/d3485a6223541986833f63906b4e455b852750a4/'
              )
            );
          }
          this.web3Initialized = true;
          resolve();
        }
      });
    }

    return this.web3InitPromise;
  }

  async autoConnect() {
    // console.log('Intentando conexión automática...');
    try {
      if (typeof window.ethereum !== 'undefined') {
        // Comprobar si hay cuentas ya autorizadas
        const accounts = await window.ethereum.request({
          method: 'eth_accounts', // No muestra prompt
        });

        if (accounts && accounts.length > 0) {
          console.log('Wallet ya autorizada:', accounts[0]);
          // Comprobar si la red es correcta
          const chainId = await window.ethereum.request({
            method: 'eth_chainId',
          });
          this.chainId = chainId;

          if (this.chainIds.includes(chainId)) {
            // Red correcta, establecer cuenta
            this.addressUser.next(accounts[0]);
            this.listening();
            return true;
          } else {
            // Red incorrecta, pero wallet conectada
            console.log('Red incorrecta, cambiando a Polygon automáticamente');
            try {
              await window.ethereum.request({
                method: 'wallet_switchEthereumChain',
                params: [{ chainId: '0x89' }],
              });
              // Si no hay error, ya estamos en la red correcta
              this.addressUser.next(accounts[0]);
              this.chainId = '0x89';
              this.listening();
              return true;
            } catch (switchError: any) {
              // Si la red no está configurada, añadirla
              if (switchError.code === 4902) {
                try {
                  await this.addPolygonNetwork();
                  this.addressUser.next(accounts[0]);
                  this.chainId = '0x89';
                  this.listening();
                  return true;
                } catch (addError) {
                  console.error('Error añadiendo red:', addError);
                  return false;
                }
              } else {
                console.error('Error cambiando red:', switchError);
                return false;
              }
            }
          }
        } else {
          return false;
        }
      }
      return false;
    } catch (error) {
      console.error('Error en autoConnect:', error);
      return false;
    }
  }

  // Método para añadir la red Polygon
  async addPolygonNetwork() {
    const params = [
      {
        chainId: '0x89',
        chainName: 'Polygon Mainnet',
        nativeCurrency: {
          name: 'Polygon',
          symbol: 'MATIC',
          decimals: 18,
        },
        rpcUrls: ['https://polygon-rpc.com'],
        blockExplorerUrls: ['https://polygonscan.com'],
      },
    ];

    return window.ethereum.request({
      method: 'wallet_addEthereumChain',
      params,
    });
  }

  async init(hideBlockchain: boolean) {
    if (!hideBlockchain) {
      await this.autoConnect();
    }

    const isMetamaskUnlocked = await window.ethereum?._metamask?.isUnlocked();
    if (
      typeof window.ethereum !== 'undefined' &&
      isMetamaskUnlocked &&
      !hideBlockchain
    ) {
      const chainId = await window.ethereum.request({ method: 'eth_chainId' });
      if (chainId !== '0x89') {
        Swal.fire(
          'Error web3',
          'Por favor conectate a Polygon Mainnet',
          'error'
        );
        this.handleIdChain();
      }
      // console.log('inyectando proveedor metamask');
      this.web3 = new Web3(window.ethereum);
    } else {
      this.web3 = new Web3(
        new Web3.providers.HttpProvider(
          'https://prettiest-orbital-arrow.matic.quiknode.pro/d3485a6223541986833f63906b4e455b852750a4/'
        )
      );
    }
  }

  connect() {
    this.handleIdChain();
  }

  async handleIdChain() {
    const chainId: string = await window.ethereum.request({
      method: 'eth_chainId',
    });
    this.chainId = chainId;
    if (!this.chainIds.includes(chainId)) {
      const params: any = [
        {
          chainId: '0x89',
          chainName: 'Polygon Mainnet',
          nativeCurrency: {
            name: 'Polygon',
            symbol: 'MATIC',
            decimals: 18,
          },
          rpcUrls: ['https://polygon-rpc.com'],
          blockExplorerUrls: ['https://polygonscan.com'],
        },
      ];

      window.ethereum
        .request({ method: 'wallet_addEthereumChain', params })
        .then(async () => {
          const chainId: string = await window.ethereum.request({
            method: 'eth_chainId',
          });
          this.chainId = chainId;
          this.handleAccounts();
        })
        .catch(() => {
          Swal.fire({
            icon: 'error',
            title: 'Oops...',
            text: 'No se ha podido conectar tu wallet!',
          });
        });
    } else {
      this.handleAccounts();
    }
  }

  async handleAccounts() {
    const accounts: string[] = await window.ethereum.request({
      method: 'eth_requestAccounts',
    });

    if (this.chainId !== null && this.chainIds.includes(this.chainId)) {
      this.addressUser.next(accounts[0]);
    } else {
      this.addressUser.next('');
    }

    this.listening();
  }

  listening() {
    window.ethereum.on('chainChanged', async (res: string) => {
      this.chainId = res;

      if (this.chainId !== null && this.chainIds.includes(this.chainId)) {
        const accounts: string[] = await window.ethereum.request({
          method: 'eth_requestAccounts',
        });
        this.addressUser.next(accounts[0]);
      } else {
        this.addressUser.next('');

        window.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: '0x89' }],
        });
      }
    });

    window.ethereum.on('accountsChanged', (accounts: string[]) => {
      if (
        this.chainId !== null &&
        this.chainIds.includes(this.chainId) &&
        accounts.length > 0
      ) {
        this.addressUser.next(accounts[0]);
      }
    });
  }

  // Método para configurar los listeners de MetaMask
  private setupMetaMaskListeners() {
    // Verificar que ethereum existe (MetaMask instalado)
    if (typeof window.ethereum !== 'undefined') {
      // Escuchar eventos de desconexión
      window.ethereum.on('disconnect', (error: any) => {
        console.log('MetaMask disconnect:', error);
        this.handleDisconnect();
      });

      // Escuchar eventos de cambio de cuenta (ya está implementado en listening(), complementamos)
      window.ethereum.on('accountsChanged', (accounts: string[]) => {
        if (accounts.length === 0) {
          // El usuario desconectó la wallet manualmente
          console.log('Wallet desconectada (accountsChanged vacío)');
          this.handleDisconnect();
        }
      });
    }
  }

  // Manejar desconexión
  private handleDisconnect() {
    console.log('Wallet desconectada, limpiando estado');
    this.addressUser.next('');

    // Reinicializar web3 con proveedor HTTP
    this.reinitializeWeb3WithHttpProvider();
  }

  // Reinicializar web3 con proveedor HTTP cuando se desconecta la wallet
  private reinitializeWeb3WithHttpProvider() {
    try {
      this.web3 = new Web3(
        new Web3.providers.HttpProvider(
          'https://prettiest-orbital-arrow.matic.quiknode.pro/d3485a6223541986833f63906b4e455b852750a4/'
        )
      );
      console.log(
        'Web3 reinicializado con proveedor HTTP después de desconexión'
      );
    } catch (error) {
      console.error('Error al reinicializar Web3:', error);
    }
  }

  // Método para verificar si MetaMask está conectado actualmente
  async isWalletConnected(): Promise<boolean> {
    if (typeof window.ethereum === 'undefined') {
      return false;
    }

    try {
      // Verificar si el usuario ha desbloqueado MetaMask
      const isUnlocked = await window.ethereum?._metamask?.isUnlocked();
      if (!isUnlocked) {
        return false;
      }

      // Verificar si hay cuentas conectadas
      const accounts = await window.ethereum.request({
        method: 'eth_accounts',
      });
      return accounts && accounts.length > 0;
    } catch (error) {
      console.error('Error verificando conexión de wallet:', error);
      return false;
    }
  }
}
